/**
 * Punto único de integración wagmi para cuenta, cadena y conexión.
 */
import { JsonRpcProvider } from '@ethersproject/providers'
import { useCallback, useMemo } from 'react'
import { useAccount, useChainId, useConnect, useDisconnect } from 'wagmi'
import type { Connector } from 'wagmi'

import { getActiveChain, getActiveChainId } from '../../config/active'
import { getRpcUrl } from '../../config/rpc'
import { tMsg } from '@/features/locale/translate'
import { disconnectWalletSafe } from './disconnectWallet'
import { ensureAppChainInWallet, mapEnsureChainError } from './ensureAppChainInWallet'
import { WrongNetworkError } from '../../errors/WrongNetworkError'
import { ChainId } from '../../sdk'
import { instrumentEthersProvider } from '../../utils/debugRpcTraffic'
import { wagmiConfig } from '../../wagmi/config'

const NETWORK_CHAIN_ID = getActiveChainId()
const NETWORK_CHAIN_NAME = getActiveChain().displayName

export type Web3Library = JsonRpcProvider

let readOnlyProviderSingleton: JsonRpcProvider | null = null

/**
 * Instancia única de lectura ethers por pestaña (receipts y compat legacy).
 * No crear `new JsonRpcProvider` fuera de este módulo.
 */
export function getReadOnlyJsonRpcProvider(): JsonRpcProvider {
  if (!readOnlyProviderSingleton) {
    readOnlyProviderSingleton = new JsonRpcProvider(getRpcUrl(NETWORK_CHAIN_ID))
    instrumentEthersProvider(readOnlyProviderSingleton, NETWORK_CHAIN_ID)
  }
  return readOnlyProviderSingleton
}

function useReadOnlyJsonRpcProvider(): Web3Library {
  return useMemo(() => getReadOnlyJsonRpcProvider(), [])
}

export interface AppWeb3State {
  library: Web3Library
  chainId: ChainId
  account?: string
  active: boolean
  connector: Connector | undefined
  activate: (c?: Connector) => Promise<void>
  deactivate: () => Promise<void>
  /** Limpia sesión persistida si disconnect falla (paracaídas UX). */
  resetWalletSession: () => Promise<void>
  error: Error | undefined
}

/** Cadena efectiva de la app: wallet si hay sesión; si no, cadena de configuración. */
export function useAppChainId(): ChainId {
  const { isConnected } = useAccount()
  const walletChainId = useChainId()
  return (isConnected ? walletChainId : NETWORK_CHAIN_ID) as ChainId
}

/**
 * Estado agregado compatible con el antiguo `useActiveWeb3React`.
 * Preferir `useAccount` / `useAppChainId` en código nuevo.
 */
export function useAppWeb3(): AppWeb3State {
  const { address, isConnected, connector } = useAccount()
  const walletChainId = useChainId()
  const library = useReadOnlyJsonRpcProvider()
  const { connectAsync, connectors, error: connectError } = useConnect()
  const { disconnectAsync } = useDisconnect()

  const injectedConnector = useMemo(
    () => connectors.find((c) => c.type === 'injected' || c.id === 'injected') ?? connectors[0],
    [connectors],
  )

  const activate = useCallback(
    async (c?: Connector) => {
      const target = c ?? injectedConnector
      if (!target) return
      await connectAsync({ connector: target, chainId: NETWORK_CHAIN_ID })
      try {
        await ensureAppChainInWallet(target)
      } catch (error) {
        throw mapEnsureChainError(error)
      }
    },
    [connectAsync, injectedConnector],
  )

  const deactivate = useCallback(async () => {
    try {
      await disconnectAsync()
    } catch {
      /* conector roto: limpiar persistencia */
    }
    await disconnectWalletSafe(wagmiConfig)
  }, [disconnectAsync])

  const resetWalletSession = useCallback(async () => {
    await deactivate()
  }, [deactivate])

  const wrongNetwork = isConnected && walletChainId !== NETWORK_CHAIN_ID

  const error = useMemo(() => {
    if (connectError) return connectError as Error
    if (wrongNetwork) {
      return new WrongNetworkError(
        tMsg().wallet.error.wrongNetworkMessage(NETWORK_CHAIN_NAME, NETWORK_CHAIN_ID, walletChainId),
      )
    }
    return undefined
  }, [connectError, wrongNetwork, walletChainId])

  const chainId = (isConnected ? walletChainId : NETWORK_CHAIN_ID) as ChainId

  return {
    library,
    chainId,
    account: address ?? undefined,
    active: isConnected,
    connector: connector ?? undefined,
    activate,
    deactivate,
    resetWalletSession,
    error,
  }
}

export { useReadOnlyJsonRpcProvider }
